import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { AuthUser } from '../models/auth.models';

@Injectable({ providedIn: 'root' })
export class SessionService {
    private static readonly ACTIVE_COMPANY_KEY = 'dentsys_active_company';
    private static readonly ACTIVE_BRANCH_KEY = 'dentsys_active_branch';
    private static readonly ACTIVE_COMPANY_NAME_KEY = 'dentsys_active_company_name';
    private static readonly ACTIVE_BRANCH_NAME_KEY = 'dentsys_active_branch_name';
    private static readonly TOKEN_SAVED_AT_KEY = 'dentsys_token_saved_at';

    getToken(): string | null {
        return localStorage.getItem(environment.tokenKey);
    }

    getRefreshToken(): string | null {
        return localStorage.getItem(environment.refreshTokenKey);
    }

    saveTokens(accessToken: string, refreshToken: string): void {
        localStorage.setItem(environment.tokenKey, accessToken);
        localStorage.setItem(environment.refreshTokenKey, refreshToken);
        localStorage.setItem(SessionService.TOKEN_SAVED_AT_KEY, String(Date.now()));
    }

    getTokenSavedAt(): number | null {
        const raw = localStorage.getItem(SessionService.TOKEN_SAVED_AT_KEY);
        return raw ? Number(raw) : null;
    }

    saveUser(user: AuthUser): void {
        localStorage.setItem('dentsys_user', JSON.stringify(user));
    }

    getUser(): AuthUser | null {
        const raw = localStorage.getItem('dentsys_user');
        return raw ? (JSON.parse(raw) as AuthUser) : null;
    }

    getIdCompany(): number | null {
        return this.getUser()?.idCompany ?? null;
    }

    getIdBranch(): number | null {
        return this.getUser()?.idBranch ?? null;
    }

    // ─── Tenant activo (empresa/sucursal efectiva) ───────────────────────────
    // Para SUPER_ADMIN la empresa/sucursal activa se cambia en runtime sin tocar
    // el token. Para el resto de roles, siempre vale la del propio usuario.

    getActiveCompanyId(): number | null {
        const raw = localStorage.getItem(SessionService.ACTIVE_COMPANY_KEY);
        return raw ? Number(raw) : null;
    }

    getActiveBranchId(): number | null {
        const raw = localStorage.getItem(SessionService.ACTIVE_BRANCH_KEY);
        return raw ? Number(raw) : null;
    }

    getActiveCompanyName(): string | null {
        return localStorage.getItem(SessionService.ACTIVE_COMPANY_NAME_KEY);
    }

    getActiveBranchName(): string | null {
        return localStorage.getItem(SessionService.ACTIVE_BRANCH_NAME_KEY);
    }

    setActiveTenant(
        companyId: number | null,
        branchId: number | null,
        companyName?: string | null,
        branchName?: string | null
    ): void {
        if (companyId) {
            localStorage.setItem(SessionService.ACTIVE_COMPANY_KEY, String(companyId));
        } else {
            localStorage.removeItem(SessionService.ACTIVE_COMPANY_KEY);
        }
        if (branchId) {
            localStorage.setItem(SessionService.ACTIVE_BRANCH_KEY, String(branchId));
        } else {
            localStorage.removeItem(SessionService.ACTIVE_BRANCH_KEY);
        }
        if (companyName) {
            localStorage.setItem(SessionService.ACTIVE_COMPANY_NAME_KEY, companyName);
        } else {
            localStorage.removeItem(SessionService.ACTIVE_COMPANY_NAME_KEY);
        }
        if (branchName) {
            localStorage.setItem(SessionService.ACTIVE_BRANCH_NAME_KEY, branchName);
        } else {
            localStorage.removeItem(SessionService.ACTIVE_BRANCH_NAME_KEY);
        }
    }

    clearActiveTenant(): void {
        localStorage.removeItem(SessionService.ACTIVE_COMPANY_KEY);
        localStorage.removeItem(SessionService.ACTIVE_BRANCH_KEY);
        localStorage.removeItem(SessionService.ACTIVE_COMPANY_NAME_KEY);
        localStorage.removeItem(SessionService.ACTIVE_BRANCH_NAME_KEY);
    }

    getRoles(): string[] {
        return this.getUser()?.roles ?? [];
    }

    hasRole(role: string): boolean {
        return this.getRoles().includes(role);
    }

    isSuperAdmin(): boolean {
        return this.hasRole('ROLE_SUPER_ADMIN');
    }

    isAdmin(): boolean {
        return this.isSuperAdmin() || this.hasRole('ROLE_ADMIN');
    }

    isLoggedIn(): boolean {
        return !!this.getToken();
    }

    clearSession(): void {
        localStorage.removeItem(environment.tokenKey);
        localStorage.removeItem(environment.refreshTokenKey);
        localStorage.removeItem('dentsys_user');
        this.clearActiveTenant();
    }
}
